import {
  anchorHeadings,
  isLinkAddress,
  MARKDOWN_EXTENSIONS,
  type MarkdownExtension,
  type MarkdownTree,
  noteIdPrefix,
  picturesAsWords,
} from '@app/formats'
import type { Root as HastRoot } from 'hast'
import { toJsxRuntime } from 'hast-util-to-jsx-runtime'
import { toHast } from 'mdast-util-to-hast'
import { Fragment, type ReactNode, useMemo } from 'react'
import { jsx, jsxs } from 'react/jsx-runtime'
import ReactMarkdown, { type Options } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { FormatRendering } from '../FormatView'
import { timeMoments } from './markdown-moments'
import { HEBREW, RIGHT_TO_LEFT, STRONG } from './reading-direction'

/**
 * Markdown, drawn.
 *
 * The adapter between the format's definition and the library this app draws
 * it with. What markdown IS — its extensions, which headings take an anchor
 * and what each one's id is, which addresses a link may hold — is the
 * definition's; this file says what each of those is called here, and what an
 * element is dressed in. It is reached through `FormatView`, never by a screen.
 */

type RemarkPlugin = NonNullable<Options['remarkPlugins']>[number]
type RehypePlugin = NonNullable<Options['rehypePlugins']>[number]

/** What this renderer calls each extension the format names. */
const EXTENSIONS: Record<MarkdownExtension, RemarkPlugin> = {
  gfm: remarkGfm,
}

const REMARK = MARKDOWN_EXTENSIONS.map((name) => EXTENSIONS[name])

/**
 * Settles the tree about to be drawn with the functions the outline runs:
 * every picture becomes its words, and every heading takes its anchor.
 *
 * Both are the definition's, done over this very tree, so the page and a
 * contents beside it cannot hold different ids — or different words — for one
 * heading.
 */
const anchored =
  (scope: string): RemarkPlugin =>
  () =>
  (tree: MarkdownTree) => {
    picturesAsWords(tree)
    anchorHeadings(tree, scope)
  }

/**
 * The shape of a node in the tree of elements, structurally.
 *
 * The library's own type says this better, but it belongs to a transitive
 * dependency and Bun's isolated linker does not place it in this package's
 * `node_modules`.
 */
type Element = {
  type: string
  tagName?: string
  value?: string
  properties?: Record<string, unknown>
  children?: Element[]
}

/**
 * The blocks that hold words, each reading in a direction of its own.
 *
 * Each takes it from its own first letter, so a Hebrew paragraph in an English
 * body is laid out right to left with its punctuation at its own end. A
 * direction set once on the whole body lays every block out in the direction
 * of the first.
 */
const HOLDS_WORDS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'td',
  'th',
])

/**
 * The blocks that hold other blocks: an item, a quote, a list, a table.
 *
 * What each draws of its own — a marker, a rule down its edge, the side its
 * items are indented from, the side its first column stands on — sits on the
 * side its words begin. The browser cannot say which that is: asked, it passes
 * over every block inside that reads its own way, which is all of them, and
 * answers left to right. So the first letter is read here, through the blocks
 * inside.
 */
const HOLDS_BLOCKS = new Set(['li', 'blockquote', 'ul', 'ol', 'table'])

/** Of those, the ones that take a direction even where nothing says which. */
const ASKS_THE_BROWSER = new Set(['li', 'blockquote'])

/** Code reads left to right whatever surrounds it: `foo(bar);` has one order. */
const READS_LEFT_TO_RIGHT = new Set(['pre', 'code'])

const tagOf = (node: Element) =>
  node.type === 'element' ? (node.tagName ?? '') : ''

/**
 * The first character a block reads by, in the text the page draws.
 *
 * For a block of words it is the one the browser takes the direction from: the
 * first in the block's own text, passing over anything inside that reads in a
 * direction of its own. Raw HTML counts as the characters typed, since that is
 * what is drawn. For a block of blocks, `through` reads on into them. Code is
 * passed over either way.
 */
function firstStrong(block: Element, through: boolean): string {
  const waiting = [...(block.children ?? [])].reverse()
  for (let node = waiting.pop(); node; node = waiting.pop()) {
    if (node.type === 'text' || node.type === 'raw') {
      const strong = STRONG.exec(node.value ?? '')?.[0]
      if (strong) return strong
      continue
    }
    const tag = tagOf(node)
    if (READS_LEFT_TO_RIGHT.has(tag)) continue
    if (!through && (HOLDS_WORDS.has(tag) || HOLDS_BLOCKS.has(tag))) continue
    for (const child of [...(node.children ?? [])].reverse()) {
      waiting.push(child)
    }
  }
  return ''
}

/**
 * What a block carries for its direction and its script.
 *
 * A block whose deciding letter is Hebrew takes the Hebrew face, and the
 * stylesheet needs something to know one by. It cannot ask the direction:
 * `:dir()` is rewritten at build time into a list of `:lang()`s, and a block
 * whose direction is read from its letters declares no language. So the block
 * says what it is written in, read off the same character its direction comes
 * from. Right to left in another script, or a mark before Latin letters, sets
 * the direction and not the face: the Hebrew face is for Hebrew and the Latin
 * set beside it.
 */
function reading(node: Element): Record<string, unknown> | null {
  const tag = tagOf(node)
  if (READS_LEFT_TO_RIGHT.has(tag)) return { dir: 'ltr' }
  if (HOLDS_WORDS.has(tag)) {
    const hebrew = HEBREW.test(firstStrong(node, false))
    return { dir: 'auto', dataScript: hebrew ? 'hebrew' : undefined }
  }
  if (HOLDS_BLOCKS.has(tag)) {
    const first = firstStrong(node, true)
    if (RIGHT_TO_LEFT.test(first)) {
      return {
        dir: 'rtl',
        dataScript: HEBREW.test(first) ? 'hebrew' : undefined,
      }
    }
    return ASKS_THE_BROWSER.has(tag) ? { dir: 'auto' } : null
  }
  return null
}

/**
 * The id the renderer gives the heading over a body's footnotes, which it
 * writes the same for every body. Two bodies on a page would share it, so it
 * is moved among the document's footnote ids.
 */
const FOOTNOTES = 'footnote-label'

function dressTree(tree: Element, scope: string) {
  timeMoments(tree)
  const label = `${noteIdPrefix(scope)}${FOOTNOTES}`
  const waiting: Element[] = [tree]
  for (let node = waiting.pop(); node; node = waiting.pop()) {
    const read = reading(node)
    if (read) node.properties = { ...node.properties, ...read }
    if (node.properties?.id === FOOTNOTES) node.properties.id = label
    /* Held as a list, since an element may be described by several. */
    const described = node.properties?.ariaDescribedBy
    if (node.properties && Array.isArray(described)) {
      node.properties.ariaDescribedBy = described.map((id) =>
        id === FOOTNOTES ? label : id,
      )
    }
    for (const child of node.children ?? []) waiting.push(child)
  }
}

const dressed =
  (scope: string): RehypePlugin =>
  () =>
  (tree: Element) => {
    dressTree(tree, scope)
  }

/**
 * A link, where its address is one a link may hold; its text, where it is not.
 *
 * It opens in a tab of its own and hands the page it opens nothing: a body may
 * be written by a model and read by somebody else, so where a link leads is
 * somewhere this app has not been.
 *
 * The way from a footnote's marker to the note and back is a link the RENDERER
 * wrote, to an id it made inside this document. No body can write one — the
 * marks that say so are attributes, and markdown has no way to set them — so
 * it stays a link, on this page.
 */
function Link({
  href,
  id,
  children,
  'data-footnote-ref': toNote,
  'data-footnote-backref': fromNote,
  'aria-describedby': describedBy,
  'aria-label': label,
}: {
  href?: string
  id?: string
  children?: ReactNode
  'data-footnote-ref'?: unknown
  'data-footnote-backref'?: unknown
  'aria-describedby'?: string
  'aria-label'?: string
}) {
  if ((toNote !== undefined || fromNote !== undefined) && href) {
    return (
      <a href={href} id={id} aria-describedby={describedBy} aria-label={label}>
        {children}
      </a>
    )
  }
  if (!isLinkAddress(href)) return <>{children}</>
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  )
}

/**
 * A picture is drawn as its description and its address, in words.
 *
 * Drawing one is a request the READER's browser makes, to whatever address the
 * text names: an address inside the app is a request in the reader's name. No
 * surface that shows formatted text needs a picture, so none is fetched. The
 * format turns every picture into those words before the tree is drawn; this
 * is what an element reaching here anyway is drawn as.
 */
function Picture({ alt, src }: { alt?: string; src?: string }) {
  return <>{[alt, src].filter(Boolean).join(' ')}</>
}

const COMPONENTS = { a: Link, img: Picture }

/** Draw an already parsed tree without reading its source a second time. */
export function MarkdownTreeView({
  tree,
  scope,
}: {
  tree: MarkdownTree
  scope: string
}) {
  return useMemo(() => {
    /* Heading anchors belong to this occurrence on the page, so they are added
       after the worker returns. Clone first: a tree received through props is
       its caller's value, not scratch storage for this renderer. */
    const settled = structuredClone(tree)
    anchorHeadings(settled, scope)
    const hast = toHast(settled, {
      allowDangerousHtml: true,
      clobberPrefix: noteIdPrefix(scope),
    }) as HastRoot
    dressTree(hast as Element, scope)

    /* Raw HTML is markdown's text here, never markup. The ordinary renderer
       makes the same change inside react-markdown. */
    const waiting: Element[] = [hast as Element]
    for (let node = waiting.pop(); node; node = waiting.pop()) {
      const children = node.children ?? []
      for (let i = 0; i < children.length; i++) {
        const child = children[i]
        if (child.type === 'raw') {
          children[i] = { type: 'text', value: child.value ?? '' }
        } else {
          waiting.push(child)
        }
      }
    }

    return toJsxRuntime(hast, {
      Fragment,
      components: COMPONENTS,
      ignoreInvalidStyle: true,
      jsx,
      jsxs,
      passKeys: true,
      passNode: true,
    })
  }, [scope, tree])
}

function ImmediateMarkdownView({ value, scope }: FormatRendering) {
  const remark = useMemo(() => [...REMARK, anchored(scope)], [scope])
  /* Footnotes are given ids too, inside the document's scope so two documents
     on a page never share one, and under a prefix no heading's id can hold. */
  const footnotes = useMemo(
    () => ({ clobberPrefix: noteIdPrefix(scope) }),
    [scope],
  )
  const rehype = useMemo(() => [dressed(scope)], [scope])
  return (
    /* The source is handed over exactly as written: a body rewritten on its way
       to the renderer is a body that renders as something nobody typed. */
    <ReactMarkdown
      remarkPlugins={remark}
      rehypePlugins={rehype}
      remarkRehypeOptions={footnotes}
      components={COMPONENTS}
    >
      {value}
    </ReactMarkdown>
  )
}

export function MarkdownView({ value, scope, tree }: FormatRendering) {
  return tree ? (
    <MarkdownTreeView tree={tree} scope={scope} />
  ) : (
    <ImmediateMarkdownView value={value} scope={scope} />
  )
}
